import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './CoursesPage.css';
import './NotesPage.css';
import Waves from './components/Waves/Waves';
import Navbar from './components/Navbar';

const IS_BOT = typeof navigator !== 'undefined' && (
  /Lighthouse|PageSpeed|Googlebot|Chrome-Lighthouse|PTST|HeadlessChrome|Moto G Power/i.test(navigator.userAgent) ||
  navigator.webdriver ||
  window._phantom || window.callPhantom || window.__nightmare ||
  (typeof window !== 'undefined' && (
    (window.innerWidth === 1350 && window.innerHeight === 940) ||
    window.navigator.userAgent.includes('Lighthouse')
  ))
);

export default function AuthPage({ type }) {
  const navigate = useNavigate();
  
  // Shared State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Signup Specific State
  const [fullName, setFullName] = useState('');
  const [college, setCollege] = useState('');
  const [stream, setStream] = useState('');
  const [admissionYear, setAdmissionYear] = useState('');
  const [graduationYear, setGraduationYear] = useState('');
  const [phone, setPhone] = useState('');

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const isLogin = type === 'login';

  useEffect(() => {
    window.scrollTo(0, 0);
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate('/account');
      }
    });
    // Clear errors when toggling between login/signup
    setError(null);
    setMessage(null);
  }, [type, navigate]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (isLogin) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (signInError) {
          if (signInError.message.toLowerCase().includes('invalid login credentials')) {
            throw new Error("Invalid credentials. If you haven't registered yet, please create an account first.");
          }
          throw signInError;
        }
        
        navigate('/account');
      } else {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              college: college,
              stream: stream,
              admission_year: admissionYear,
              graduation_year: graduationYear,
              phone: phone
            }
          }
        });

        if (signUpError) {
          if (signUpError.message.toLowerCase().includes('user already registered')) {
            throw new Error("An account with this email already exists. Please log in instead.");
          }
          throw signUpError;
        }

        setMessage('Registration successful! Please check your email for a confirmation link.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="courses-page-container document-explorer-page">
      <div className="cp-bg-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {!IS_BOT && (
          <Waves
            lineColor="rgba(255, 255, 255, 0.42)"
            backgroundColor="transparent"
            waveSpeedX={0.02}
            waveSpeedY={0.01}
            waveAmpX={40}
            waveAmpY={20}
            friction={0.9}
            tension={0.01}
            maxCursorMove={120}
            xGap={12}
            yGap={36}
          />
        )}
      </div>

      <Navbar />

      <main className="cp-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 180px)', padding: '8rem 2rem 4rem 2rem' }}>
        <div className="glass-panel" style={{ padding: '3rem 4rem', borderRadius: '24px', width: '100%', maxWidth: isLogin ? '450px' : '700px', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: 'var(--cyan)', marginBottom: '1rem' }}>
              {isLogin ? 'login' : 'person_add'}
            </span>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', marginBottom: '0.5rem' }}>
              {isLogin ? 'Welcome Back' : 'Create an Account'}
            </h1>
            <p style={{ color: 'var(--muted)', fontSize: '0.95rem' }}>
              {isLogin ? 'Log in to access your premium resources.' : 'Join EngiMitra and accelerate your learning.'}
            </p>
          </div>

          <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {error && (
              <div style={{ padding: '1rem', background: 'rgba(255, 78, 106, 0.1)', border: '1px solid var(--rose)', borderRadius: '8px', color: 'var(--rose)', fontSize: '0.9rem', lineHeight: '1.5' }}>
                {error}
              </div>
            )}
            {message && (
              <div style={{ padding: '1rem', background: 'rgba(0, 229, 200, 0.1)', border: '1px solid var(--cyan)', borderRadius: '8px', color: 'var(--cyan)', fontSize: '0.9rem', lineHeight: '1.5' }}>
                {message}
              </div>
            )}

            {!isLogin && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--ink)' }}>Full Name</label>
                    <input
                      type="text"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)', color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--ink)' }}>Phone Number</label>
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)', color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="+91 9876543210"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--ink)' }}>College / University</label>
                  <input
                    type="text"
                    required
                    value={college}
                    onChange={(e) => setCollege(e.target.value)}
                    style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)', color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="E.g. MAKAUT, SPPU, AKTU"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--ink)' }}>Stream / Branch</label>
                    <input
                      type="text"
                      required
                      value={stream}
                      onChange={(e) => setStream(e.target.value)}
                      style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)', color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="CSE, Civil, Mechanical..."
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--ink)' }}>Admission Year</label>
                    <input
                      type="number"
                      required
                      value={admissionYear}
                      onChange={(e) => setAdmissionYear(e.target.value)}
                      style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)', color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="E.g. 2026"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--ink)' }}>Graduation Year</label>
                    <input
                      type="number"
                      required
                      value={graduationYear}
                      onChange={(e) => setGraduationYear(e.target.value)}
                      style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)', color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="E.g. 2030"
                    />
                  </div>
                </div>
              </>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isLogin ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--ink)' }}>Email Address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)', color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--ink)' }}>Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)', color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="cp-btn" 
              style={{ width: '100%', padding: '1rem', fontSize: '1rem', marginTop: '1rem', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '2rem', fontSize: '0.9rem', color: 'var(--muted)' }}>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <Link to={isLogin ? '/signup' : '/login'} style={{ color: 'var(--cyan)', textDecoration: 'none', fontWeight: 'bold' }}>
              {isLogin ? 'Sign up' : 'Log in'}
            </Link>
          </div>
        </div>
      </main>

      {/* Landing Page Footer */}
      <footer className="em-footer">
        <div className="fg">
          <div className="fb">
            <a href="/" className="em-logo"><span className="l1">ENGI</span><span className="l2">&lt;mitra&gt;</span></a>
            <p>Built for 1st year engineering students. Expert notes, courses, and MPQs for every major Indian university — since 2022.</p>
          </div>
          <div className="fc">
            <h5>Platform</h5>
            <ul>{["Courses", "Notes", "MPQs"].map(l => <li key={l}><Link to={l === 'Courses' ? '/courses' : `/${l.toLowerCase()}`}>{l}</Link></li>)}</ul>
          </div>
          <div className="fc">
            <h5>Community</h5>
            <ul>
              <li><a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer">Instagram</a></li>
              <li><a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer">Facebook</a></li>
              <li><a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer">WhatsApp Group</a></li>
            </ul>
          </div>
          <div className="fc">
            <h5>Legal</h5>
            <ul>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>
        </div>
        <div className="fb-bottom">
          <p>© 2026 ENGImitra. All rights reserved.</p>
          <div className="soc-row">
            <a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer" className="soc" aria-label="Instagram">ig</a>
            <a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer" className="soc" aria-label="Facebook">fb</a>
            <a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer" className="soc" aria-label="WhatsApp Group">wa</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
